/* eslint-disable react-hooks/exhaustive-deps */
import WarningTriangle from '@ingka/ssr-icon/paths/warning-triangle';
import { ComponentIds } from '@inter-ikea-core/core';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import {
  HelpLinesComponentEditMode,
  HelpLinesComponentVisible,
  ShapeTypeEnum
} from '@inter-ikea-storageone/range';
import { reportAutoFillFrameDepthChange, reportAutoFillLegsToggled } from 'analytics/reporters';
import { getFilterState, getMarketProducts, getSelectedGroupId } from 'ducks/products';
import {
  compareAutoFillResults,
  getSelectedLegOrDefault,
  getTopHeight,
  reportAutoFillRecommendation,
  setRecommendationToScene,
  setSelectedLegToScene
} from '../AutoFillUtils';
import SkapaSwitch from 'components/Switch/SkapaSwitch';
import { useCore } from 'contexts/core';
import {
  autoFillDepth,
  isAutofillActive,
  setAutoFillDepth,
  setAutofillActive
} from 'rangeConfig/platsa/features/Autofill/Autofill';
import { helpLinesShape } from 'ducks/helpLines';
import { useAppDispatch } from 'ducks/hooks';
import { hideMeasurements, showMeasurements } from 'ducks/layout';
import usePrevious from 'hooks/usePrevious';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import Box from 'ui/Box';
import Divider from 'ui/Divider/Divider';
import SkapaInlineMessage, { InlineMessageVariantEnum } from 'ui/InlineMessage/SkapaInlineMessage';
import SizeToggle from 'ui/SizeToggle/SizeToggle';
import Title from 'ui/Typography/Title';
import { adjustMeasurementsSystem } from 'utils/adjustMeasurementsSystem';
import { rangeIsPax, rangeIsPlatsa } from 'utils/queryUtils';
import {
  AutoFillRecommendation,
  AutoFillRecommendationTypes,
  getRecommendations
} from '../AutoFillAlgorithm';
import { useTranslations } from 'hooks/useTranslations';
import { IStorageOneTranslations, useServiceSettings } from 'contexts/serviceSettings';

const Text = styled.div`
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  line-height: 1.5;
`;

const StyledToggle = styled(Text)`
  display: flex;
  margin-bottom: 1rem;
  justify-content: space-between;
`;

const LEGS_HIGH = 110;
const LEGS_LOW = 18;
const LEGS_NONE = 0;

const AutoFillOptimiseSpace = () => {
  const autofillActive = useSelector(isAutofillActive);
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();
  const allProducts = useSelector(getMarketProducts);
  const currentShape = useSelector(helpLinesShape);
  const prevShape = usePrevious(currentShape);
  const [
    currentAutoFillRecommendation,
    setCurrentAutoFillRecommendation
  ] = useState<AutoFillRecommendation | null>(null);
  const coreApi = useCore();
  const { translations } = useServiceSettings();
  const [autoFillRecommendations, setAutoFillRecommendations] = useState<
    AutoFillRecommendation[]
  >();
  const autoFillEnabled = rangeIsPlatsa();
  const useAutoFill = autoFillEnabled && rangeIsPlatsa();
  const depth = useSelector(autoFillDepth);
  const [legsHeight, setLegsHeight] = useState<number>(
    rangeIsPlatsa()
      ? coreApi.products.getDefaultLeg() !==
        getSelectedLegOrDefault(coreApi, allProducts)?.coreProductId
        ? LEGS_HIGH
        : LEGS_LOW
      : LEGS_NONE
  );
  const legsHeightRef = useRef(legsHeight);
  const [showWarning, setShowWarning] = useState<boolean>(
    coreApi.entities.getByComponent(ComponentIds.FRAME_COMPONENT_ID)?.length > 0
  );
  const [selectedRecommendation, setSelectedRecommendation] = useState<
    AutoFillRecommendationTypes
  >();
  const { unit, useMetric } = useTranslations();
  const DEPTH_OPTIONS = getRangeSpecificDepthOptions(useMetric, translations, unit);
  const showLegsToggle = rangeIsPlatsa();
  const filterState = useSelector(getFilterState);
  const selectedGroupId = useSelector(getSelectedGroupId);

  const calculateRecommendations = useCallback(
    (depth: number, legsHeight: number) => {
      if (useAutoFill) {
        setAutoFillRecommendations(
          getRecommendations(
            coreApi,
            depth,
            allProducts,
            legsHeight,
            getTopHeight(coreApi, allProducts),
            filterState(selectedGroupId, 'design').design
          )
        );
      }
    },
    [coreApi]
  );

  const isPreviousAndNewRecommendationEqual = (
    newAutoFillRecommendation: AutoFillRecommendation
  ) => {
    if (!currentAutoFillRecommendation) return false;

    return compareAutoFillResults(currentAutoFillRecommendation, newAutoFillRecommendation);
  };

  useEffect(() => {
    dispatch(hideMeasurements());

    return () => {
      dispatch(showMeasurements());
      dispatch(setAutofillActive(false));
    };
  }, []);

  // Autofill when entering component
  useEffect(() => {
    autoFillWhenEnteringComponent();
  }, []);

  /**
   * When mounting the component and no frames exist in the scene,
   * we turn on autofill and start filling the current wall shape.
   */
  const autoFillWhenEnteringComponent = () => {
    const framesInScene = coreApi.entities.getByComponent(ComponentIds.FRAME_COMPONENT_ID);
    if (framesInScene.length === 0) {
      if (autofillActive) {
        setSelectedRecommendation(AutoFillRecommendationTypes.MixedFrames);
        calculateAndApplyRecommendation();
      }
    }
  };

  // Triggered when user changes shape form. E.g. going from square shape to vaulted shape
  useEffect(() => {
    // Don't enter if state is equal or shape is set to off
    if (prevShape === currentShape || currentShape === ShapeTypeEnum.off) return;

    if (autofillActive) {
      calculateAndApplyRecommendation();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentShape]);

  useEffect(() => {
    if (autofillActive) {
      calculateAndApplyRecommendation();
    }
  }, [depth, legsHeight]);

  // If a recommendation has been set to the scene, actions like shape resize shall refill it with
  // updated recommendation
  useEffect(() => {
    if (autofillActive) {
      const recommendation = autoFillRecommendations?.find(r => r.type === selectedRecommendation);

      if (!recommendation) return;

      if (isPreviousAndNewRecommendationEqual(recommendation)) {
        coreApi.entities.showEntities([ComponentIds.FRAME_COMPONENT_ID], true);
      } else {
        coreApi.performLongCoreOperation(() => {
          coreApi.history.historyAction(() => {
            setRecommendation(recommendation);
          });
        });
      }
    }
  }, [autoFillRecommendations]);

  const setRecommendation = (autoFillRecommendation: AutoFillRecommendation) => {
    setRecommendationToScene(coreApi, autoFillRecommendation);
    reportAutoFillRecommendation(autoFillRecommendation, legsHeight === LEGS_HIGH, depth);
    setCurrentAutoFillRecommendation(autoFillRecommendation);
    setShowWarning(false);
  };

  const calculateAndApplyRecommendation = () => {
    calculateRecommendations(depth, legsHeightRef.current);
  };

  useEffect(() => {
    return coreApi.subscribe('helpLinesShapeResizeFinished', calculateAndApplyRecommendation);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLegsChange = () => {
    const legsToApplyToscene =
      legsHeight === LEGS_HIGH
        ? coreApi.products.getDefaultLeg()
        : coreApi.products.getDefaultHighLegForAutoFill();

    setSelectedLegToScene(coreApi, legsToApplyToscene);
    reportAutoFillLegsToggled({ enable: legsHeight === LEGS_HIGH });

    const newLegsHeight = legsHeight === LEGS_HIGH ? LEGS_LOW : LEGS_HIGH;

    setLegsHeight(newLegsHeight);
    legsHeightRef.current = newLegsHeight;
  };

  const handleDepthChange = (depth: number) => {
    dispatch(setAutoFillDepth(depth));
    reportAutoFillFrameDepthChange({ depth });
  };

  // Function to handle toggle and recommendation
  const handleToggleAutofill = () => {
    const scene = coreApi.entities.getScene();
    if (!autofillActive) {
      setSelectedRecommendation(AutoFillRecommendationTypes.MixedFrames);
      scene?.addComponent(new HelpLinesComponentVisible(true));
      scene?.addComponent(new HelpLinesComponentEditMode(true));

      calculateAndApplyRecommendation();
    } else {
      reportAutofillToggledOff(legsHeight === LEGS_HIGH, depth);
    }
    dispatch(setAutofillActive(!autofillActive));
  };

  const autoFillDisabled = currentShape === ShapeTypeEnum.off;

  return useAutoFill ? (
    <>
      {showWarning && (
        <Box mt="xs" mb="xl">
          <SkapaInlineMessage
            body={formatMessage({ id: 'storageone.autofillFramesReplaceWarning' })}
            variant={InlineMessageVariantEnum.CAUTIONARY}
            ssrIcon={WarningTriangle}
          />
        </Box>
      )}
      {!rangeIsPax() && (
        <StyledToggle>
          <Box mr="xl">
            <Text> {formatMessage({ id: 'storageone.autofillOptimiseToggleSubtitle' })}</Text>
          </Box>
          <SkapaSwitch
            id="toggle-autofill"
            value={formatMessage({ id: 'storageone.autofillOptimiseToggleTitle' })}
            label=""
            handleChange={handleToggleAutofill}
            checked={autofillActive}
            disabled={autoFillDisabled}
          />
        </StyledToggle>
      )}

      {rangeIsPlatsa() && autofillActive && !autoFillDisabled && (
        <>
          <Divider />
          <Box mt="md" mb="l">
            <Title id="autofill-change-depth-heading" type={ThemeFontStyleTypeEnum.headingXSmall}>
              {formatMessage({ id: 'storageone.autofillChangeDepthTitle' })}
            </Title>
            <SizeToggle
              sizes={DEPTH_OPTIONS}
              currentSize={depth}
              onChangeSize={handleDepthChange}
            />
          </Box>
          {showLegsToggle && (
            <>
              <Divider />
              <Box mt="md" mb="md">
                <StyledToggle>
                  <Title
                    type={ThemeFontStyleTypeEnum.headingXSmall}
                    id="autofill-include-legs-heading"
                  >
                    {formatMessage({ id: 'storageone.autofillLegsToggleTitle' })}
                  </Title>
                  <SkapaSwitch
                    id="toggle-legschange"
                    value={formatMessage({ id: 'storageone.autofillOptimiseToggleTitle' })}
                    label=""
                    handleChange={handleLegsChange}
                    checked={legsHeight === LEGS_HIGH}
                  />
                </StyledToggle>
              </Box>
            </>
          )}
        </>
      )}
    </>
  ) : (
    <></>
  );
};

const getRangeSpecificDepthOptions = (
  useMetricMeasures: boolean,
  translations: IStorageOneTranslations,
  unit: string
) => {
  if (rangeIsPlatsa()) {
    return [
      { text: adjustMeasurementsSystem(400, translations, useMetricMeasures), unit, value: 400 },
      {
        text: adjustMeasurementsSystem(550, translations, useMetricMeasures),
        unit,
        value: 550
      }
    ];
  } else if (rangeIsPax()) {
    return [
      { text: adjustMeasurementsSystem(350, translations, useMetricMeasures), unit, value: 350 },
      {
        text: adjustMeasurementsSystem(580, translations, useMetricMeasures),
        unit,
        value: 580
      }
    ];
  }
  return [];
};

const reportAutofillToggledOff = (highLegs: boolean, depth: number) => {
  reportAutoFillRecommendation(
    {
      frames: [],
      area: 0,
      nrOfFrames: 0,
      type: AutoFillRecommendationTypes.Off
    } as AutoFillRecommendation,
    highLegs,
    depth
  );
};

export default AutoFillOptimiseSpace;
